/* ============================================================
   Dungeon Hoard — procedural 8-bit sound effects
   ------------------------------------------------------------
   Chiptune blips synthesized live on the AudioContext shared with the
   music (see music.ts). Every effect is gated by the single mute toggle
   and is only ever triggered by a user gesture (click / drop), so the
   shared context resumes cleanly. No audio files.
   ============================================================ */

import { withAudio, type AudioBundle } from "./music";

type Wave = "pulse12" | "pulse25" | "pulse50" | "triangle" | "square";

interface ToneOpts {
  wave: Wave;
  f0: number; // start frequency
  f1?: number; // optional glide target
  t: number; // start time (ctx time)
  dur: number;
  peak: number;
  attack?: number;
}

const SEMI: Record<string, number> = {
  C: 0, "C#": 1, D: 2, "D#": 3, E: 4, F: 5, "F#": 6, G: 7, "G#": 8, A: 9, "A#": 10, B: 11,
};
function freq(name: string): number {
  const m = /^([A-G]#?)(\d)$/.exec(name)!;
  const midi = (Number(m[2]) + 1) * 12 + SEMI[m[1]];
  return 440 * Math.pow(2, (midi - 69) / 12);
}

function tone(a: AudioBundle, o: ToneOpts): void {
  const osc = a.ctx.createOscillator();
  const g = a.ctx.createGain();
  if (o.wave === "triangle" || o.wave === "square") osc.type = o.wave;
  else osc.setPeriodicWave(a[o.wave]);
  osc.frequency.setValueAtTime(o.f0, o.t);
  if (o.f1 !== undefined) osc.frequency.exponentialRampToValueAtTime(Math.max(1, o.f1), o.t + o.dur);
  const atk = o.attack ?? 0.005;
  g.gain.setValueAtTime(0.0001, o.t);
  g.gain.linearRampToValueAtTime(o.peak, o.t + atk);
  g.gain.exponentialRampToValueAtTime(0.0001, o.t + o.dur);
  osc.connect(g);
  g.connect(a.bus);
  osc.start(o.t);
  osc.stop(o.t + o.dur + 0.02);
}

function noiseBurst(a: AudioBundle, t: number, dur: number, peak: number, hz: number, type: BiquadFilterType): void {
  const src = a.ctx.createBufferSource();
  src.buffer = a.noise;
  const filt = a.ctx.createBiquadFilter();
  filt.type = type;
  filt.frequency.value = hz;
  const g = a.ctx.createGain();
  g.gain.setValueAtTime(peak, t);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  src.connect(filt);
  filt.connect(g);
  g.connect(a.bus);
  src.start(t);
  src.stop(t + dur + 0.02);
}

/* ---------- the effects ---------- */

/** Striking the foe — a punchy thwack (noise impact + a falling blip). */
export function sfxHit(crit = false): void {
  withAudio((a) => {
    const t = a.ctx.currentTime;
    noiseBurst(a, t, crit ? 0.16 : 0.11, crit ? 0.32 : 0.24, 1600, "highpass");
    tone(a, { wave: "square", f0: crit ? 520 : 420, f1: crit ? 90 : 120, t, dur: crit ? 0.16 : 0.12, peak: 0.22 });
    if (crit) tone(a, { wave: "pulse25", f0: 880, f1: 220, t: t + 0.02, dur: 0.18, peak: 0.16 });
  });
}

/** The foe falls — a short triumphant up-arpeggio. */
export function sfxDefeat(): void {
  withAudio((a) => {
    const t0 = a.ctx.currentTime;
    const notes = ["A4", "C5", "E5", "A5"];
    notes.forEach((nm, i) => {
      tone(a, { wave: "pulse25", f0: freq(nm), t: t0 + i * 0.08, dur: 0.16, peak: 0.2 });
    });
    tone(a, { wave: "pulse12", f0: freq("E5"), t: t0 + 0.32, dur: 0.34, peak: 0.18 });
  });
}

/** Packing a piece into the grid — a pleasant two-note "ding". */
export function sfxPack(): void {
  withAudio((a) => {
    const t = a.ctx.currentTime;
    tone(a, { wave: "pulse25", f0: freq("C5"), t, dur: 0.07, peak: 0.18 });
    tone(a, { wave: "pulse25", f0: freq("G5"), t: t + 0.06, dur: 0.12, peak: 0.18 });
  });
}

/** Leaving loot behind (dropped off the grid) — a soft low thunk. */
export function sfxDiscard(): void {
  withAudio((a) => {
    const t = a.ctx.currentTime;
    tone(a, { wave: "pulse50", f0: freq("G3"), f1: freq("C3"), t, dur: 0.16, peak: 0.16 });
    noiseBurst(a, t, 0.09, 0.08, 400, "lowpass");
  });
}

/** Rotating a piece — a tiny tick. */
export function sfxRotate(): void {
  withAudio((a) => {
    tone(a, { wave: "pulse12", f0: 900, t: a.ctx.currentTime, dur: 0.045, peak: 0.12 });
  });
}

/** Opening the You-vs-Optimizer comparison — a rising sparkle. */
export function sfxReveal(): void {
  withAudio((a) => {
    const t0 = a.ctx.currentTime;
    ["C5", "E5", "G5", "C6"].forEach((nm, i) => {
      tone(a, { wave: "pulse12", f0: freq(nm), t: t0 + i * 0.07, dur: 0.2, peak: 0.16 });
    });
  });
}

/** A soft tick as each piece drops into a comparison grid. */
export function sfxPlaceTick(optimal = false): void {
  withAudio((a) => {
    tone(a, { wave: optimal ? "pulse25" : "pulse50", f0: optimal ? 740 : 520, t: a.ctx.currentTime, dur: 0.05, peak: 0.1 });
  });
}

/** The final run summary — a fuller fanfare. */
export function sfxWin(): void {
  withAudio((a) => {
    const t0 = a.ctx.currentTime;
    const seq: Array<[string, number]> = [["C5", 0], ["E5", 0.1], ["G5", 0.2], ["C6", 0.3], ["G5", 0.42], ["C6", 0.5]];
    for (const [nm, dt] of seq) tone(a, { wave: "pulse25", f0: freq(nm), t: t0 + dt, dur: 0.22, peak: 0.18 });
    tone(a, { wave: "pulse50", f0: freq("C3"), t: t0, dur: 0.7, peak: 0.16 });
  });
}
